import { addDays, isValid, parse } from 'date-fns';

import type { Medicine, ReferenceItem } from '../../../model';
import { capitalize, formatDateRu, formatDaysPlural } from '../../../utils';
import {
  INTERVAL_DAYS_OPTIONS,
  TIMES_PER_INTERVAL_OPTIONS,
  getPartOfDay,
} from './medicine-fields';
import type { IntakeMethod, TimeWithCount } from './medicine-fields';

/**
 * Режим приёма из модалки «Добавить лекарство». Справочник частоты (200) знает
 * только «В день» и «В неделю», поэтому «раз в несколько дней» живёт на фронте,
 * а в receipt_items уходит ближайшее по смыслу значение справочника.
 */
export type FrequencyMode = 'daily' | 'interval' | 'weekly';

export type OrderMedicine = {
  totalCount: number;
  count: number;
  frequency: ReferenceItem;
  medicine: Medicine;
  time: TimeWithCount[];
  duration: number;
  /** Дата начала курса в ISO (yyyy-MM-dd) — в таком виде уходит на бэкенд. */
  start: string;
  /** «До/после/во время еды» — маппится в eating_id (справочник 205). */
  intakeMethod?: IntakeMethod;
  frequencyMode?: FrequencyMode;
  /** Сколько раз за интервал — только для режима «раз в несколько дней». */
  timesPerInterval?: number;
  /** Длина интервала в днях — только для режима «раз в несколько дней». */
  intervalDays?: number;
};

export type DaytimeId = 'morning' | 'afternoon' | 'evening';

/** «Во сколько?» — часть суток и час приёма по умолчанию. */
export const DAYTIME_OPTIONS: Array<{
  id: DaytimeId;
  label: string;
  hour: number;
  /** Как эта часть суток называется в справочнике 202 («Утро», «День»…). */
  match: RegExp;
}> = [
  { id: 'morning', label: 'Утром', hour: 8, match: /утр/i },
  { id: 'afternoon', label: 'Днём', hour: 14, match: /день|дн[её]м/i },
  { id: 'evening', label: 'Вечером', hour: 20, match: /веч/i },
];

/**
 * id части суток из справочника 202. Стенд отдаёт «Утро/День/Вечер», поэтому
 * ищем по названию: раньше сюда уходил час приёма, а это чужие id — в заказе
 * вместо времени оказывалось случайное значение другого справочника.
 */
export const resolveDaytimeId = (
  list: ReferenceItem[],
  daytime: DaytimeId,
): number | undefined => {
  const option = DAYTIME_OPTIONS.find((item) => item.id === daytime);
  if (!option) return undefined;
  return (
    list.find((item) => option.match.test(item.value))?.id ??
    // Справочник недоступен — шлём час, как делала прежняя форма.
    option.hour
  );
};

/**
 * Значение справочника частоты под выбранный режим. На стенде есть «Раз в два
 * дня» и «Раз в три дня»; интервалы, которых в справочнике нет, отправить
 * нечем — берём «В день», чтобы заказ хотя бы создался.
 */
export const resolveFrequency = (
  list: ReferenceItem[],
  mode: FrequencyMode,
  intervalDays: number,
): ReferenceItem | undefined => {
  const find = (pattern: RegExp) =>
    list.find((item) => pattern.test(item.value));
  const daily = find(/^\s*в день\s*$/i);
  const weekly = find(/^\s*в неделю\s*$/i);

  if (mode === 'weekly') return weekly ?? daily ?? list[0];
  if (mode === 'interval') {
    if (intervalDays === 7) return weekly ?? daily ?? list[0];
    const byName = {
      2: /два дня/i,
      3: /три дня/i,
    }[intervalDays];
    return (byName && find(byName)) ?? daily ?? list[0];
  }
  return daily ?? list[0];
};

export const FREQUENCY_MODE_OPTIONS: Array<{
  id: FrequencyMode;
  label: string;
}> = [
  { id: 'daily', label: 'Каждый день' },
  { id: 'interval', label: 'Раз в несколько дней' },
  { id: 'weekly', label: 'Раз в неделю' },
];

/** «Таблетка, 200 мг» — подпись препарата под названием. */
export const formatMedicineSubtitle = (medicine?: Medicine): string => {
  if (!medicine) return '';
  const form = medicine.form_type?.value ?? '';
  const dosage =
    medicine.dosage != null
      ? `${medicine.dosage} ${medicine.dosage_type?.value ?? 'мг'}`
      : '';
  return [form, dosage].filter(Boolean).join(', ');
};

/** Время приёма человеческими словами: «Утром в 8:00». */
export const formatIntakeTime = (slots: TimeWithCount[]): string =>
  slots
    .map((slot) => {
      const value = slot.value || '08:00';
      return `${getPartOfDay(value)} в ${value.replace(/^0/, '')}`;
    })
    .join(', ');

/** «Каждый день» / «Раз в несколько дней (1 раз каждые 3 дня)» / «Раз в неделю». */
export const formatFrequency = (medicine: OrderMedicine): string => {
  if (medicine.frequencyMode === 'daily') return 'Каждый день';
  if (medicine.frequencyMode === 'weekly') return 'Раз в неделю';
  if (medicine.frequencyMode === 'interval') {
    const times = medicine.timesPerInterval ?? 1;
    const days = medicine.intervalDays ?? 3;
    const timesLabel =
      TIMES_PER_INTERVAL_OPTIONS.find((o) => o.value === times)?.label ??
      `${times} раз`;
    const daysLabel =
      INTERVAL_DAYS_OPTIONS.find((o) => o.value === days)?.label ??
      `${days} ${formatDaysPlural(days)}`;
    return `Раз в несколько дней (${timesLabel} каждые ${daysLabel})`;
  }
  // Препарат без режима (например, восстановленный из старого состояния) —
  // показываем то, что пришло из справочника.
  return medicine.frequency?.value ?? '';
};

/** Строка под названием в карточке: частота / время / длительность. */
export const formatScheduleSummary = (medicine: OrderMedicine): string =>
  [
    formatFrequency(medicine),
    formatIntakeTime(medicine.time),
    `${medicine.duration} ${formatDaysPlural(medicine.duration)}`,
  ]
    .filter(Boolean)
    .join(' / ');

/** Шаг между приёмами в днях: каждый день, каждые N дней или раз в неделю. */
const getStepDays = (medicine: OrderMedicine): number => {
  if (medicine.frequencyMode === 'weekly') return 7;
  if (medicine.frequencyMode === 'interval') {
    return Math.max(1, medicine.intervalDays ?? 1);
  }
  return 1;
};

/** Больше карточек в ленту не рендерим: курс бывает и на год. */
export const INTAKE_DAYS_LIMIT = 60;

/**
 * Дни приёма курса — по ним рисуется лента пакетиков в карточке.
 * `rest` — сколько дней не поместилось в лимит.
 */
export const getIntakeDays = (
  medicine: OrderMedicine,
  limit = INTAKE_DAYS_LIMIT,
): { dates: Date[]; rest: number } => {
  const startDate = parse(medicine.start, 'yyyy-MM-dd', new Date());
  if (!medicine.start || !isValid(startDate)) return { dates: [], rest: 0 };
  const duration = Math.max(1, medicine.duration || 1);
  const step = getStepDays(medicine);
  const total = Math.floor((duration - 1) / step) + 1;
  const shown = Math.min(total, Math.max(0, limit));
  return {
    dates: Array.from({ length: shown }, (_, i) =>
      addDays(startDate, i * step),
    ),
    rest: total - shown,
  };
};

/** Дата пакетика: «20» отдельно от «Января 2026» — они разного цвета. */
export const formatIntakeDay = (
  date: Date,
): { day: string; month: string } => ({
  day: formatDateRu(date, 'd'),
  month: capitalize(formatDateRu(date, 'MMMM yyyy')),
});
